import { Box, Button, Chip, MenuItem, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import ExpandMore from '@material-ui/icons/ExpandMore';

import { ContextMenu, ObjUtils } from '@spotify/confidence-core-react';
import {
  InputMaybe,
  MetricFilterCriterionFragment,
  MetricsV1FilterCriterionAttributeCriterion,
  MetricsV1FilterCriterionInput,
  MetricsV1FilterRangeRule,
  MetricsV1FilterValue,
} from '@spotify/confidence-graphql';

import {
  MetricFilterEqRule,
  MetricFilterLikeRule,
  MetricFilterRangeRule,
  MetricFilterSetRule,
} from './RuleInputs';
import { FilterColumn } from './types';
import { RuleType, getValueType } from './useMetricFilters';

export type ValueType = keyof MetricsV1FilterValue;

export type RangeOperatorOption = {
  ruleType: 'rangeRule';
  label: string;
  isNot: boolean;
  fieldKeys: (keyof MetricsV1FilterRangeRule)[];
  isSelected: (v: MetricsV1FilterCriterionAttributeCriterion) => boolean;
};
export type BaseOperatorOption = {
  label: string;
  isNot: boolean;
  ruleType: 'setRule' | 'eqRule' | 'likeRule';
  isSelected?: (v: MetricsV1FilterCriterionAttributeCriterion) => boolean;
};

export type OperatorOption = BaseOperatorOption | RangeOperatorOption;
export const OP: Record<ValueType, OperatorOption[]> = {
  stringValue: [
    {
      label: 'is',
      isNot: false,
      ruleType: 'eqRule',
    },
    {
      label: 'is not',
      isNot: true,
      ruleType: 'eqRule',
    },
    {
      label: 'in',
      isNot: false,
      ruleType: 'setRule',
    },
    {
      label: 'not in',
      isNot: true,
      ruleType: 'setRule',
    },
    {
      label: 'like',
      isNot: false,
      ruleType: 'likeRule',
    },
    {
      label: 'not like',
      isNot: true,
      ruleType: 'likeRule',
    },
    {
      label: 'is null',
      isNot: false,
      ruleType: 'eqRule',
    },
    {
      label: 'is not null',
      isNot: true,
      ruleType: 'eqRule',
    },
  ],
  boolValue: [
    {
      label: 'is',
      isNot: false,
      ruleType: 'eqRule',
    },
    {
      label: 'is not',
      isNot: true,
      ruleType: 'eqRule',
    },
    {
      label: 'is null',
      isNot: false,
      ruleType: 'eqRule',
    },
    {
      label: 'is not null',
      isNot: true,
      ruleType: 'eqRule',
    },
  ],
  numberValue: [
    {
      label: '=',
      isNot: false,
      ruleType: 'eqRule',
    },
    {
      label: '≠',
      isNot: true,
      ruleType: 'eqRule',
    },
    {
      label: '>',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['startExclusive'],
      isSelected: v => {
        return (
          !v.rangeRule?.endExclusive &&
          !v.rangeRule?.endInclusive &&
          !!v.rangeRule?.startExclusive &&
          !v.rangeRule?.startInclusive
        );
      },
    },
    {
      label: '≥',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['startInclusive'],
      isSelected: v =>
        !v.rangeRule?.endExclusive &&
        !v.rangeRule?.endInclusive &&
        !v.rangeRule?.startExclusive &&
        !!v.rangeRule?.startInclusive,
    },
    {
      label: '<',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['endExclusive'],
      isSelected: v =>
        !!v.rangeRule?.endExclusive &&
        !v.rangeRule?.endInclusive &&
        !v.rangeRule?.startExclusive &&
        !v.rangeRule?.startInclusive,
    },
    {
      label: '≤',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['endInclusive'],
      isSelected: v =>
        !v.rangeRule?.endExclusive &&
        !!v.rangeRule?.endInclusive &&
        !v.rangeRule?.startExclusive &&
        !v.rangeRule?.startInclusive,
    },
    {
      label: 'in',
      isNot: false,
      ruleType: 'setRule',
    },
    {
      label: 'not in',
      isNot: true,
      ruleType: 'setRule',
    },
    {
      label: 'is between',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['startExclusive', 'endExclusive'],
      isSelected: v => {
        return (
          (!!v.rangeRule?.endInclusive || !!v.rangeRule?.endExclusive) &&
          (!!v.rangeRule?.startExclusive || !!v.rangeRule?.startInclusive)
        );
      },
    },
    {
      label: 'is not between',
      isNot: true,
      fieldKeys: ['startExclusive', 'endExclusive'],
      ruleType: 'rangeRule',
      isSelected: v => {
        return (
          (!!v.rangeRule?.endInclusive || !!v.rangeRule?.endExclusive) &&
          (!!v.rangeRule?.startExclusive || !!v.rangeRule?.startInclusive)
        );
      },
    },
    {
      label: 'is null',
      isNot: false,
      ruleType: 'eqRule',
    },
    {
      label: 'is not null',
      isNot: true,
      ruleType: 'eqRule',
    },
  ],
  timestampValue: [
    {
      label: 'is',
      isNot: false,
      ruleType: 'eqRule',
    },
    {
      label: 'is not',
      isNot: true,
      ruleType: 'eqRule',
    },
    {
      label: 'is after',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['startExclusive'],
      isSelected: v => {
        return (
          !v.rangeRule?.endExclusive &&
          !v.rangeRule?.endInclusive &&
          !!v.rangeRule?.startExclusive &&
          !v.rangeRule?.startInclusive
        );
      },
    },
    {
      label: 'is on or after',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['startInclusive'],
      isSelected: v =>
        !v.rangeRule?.endExclusive &&
        !v.rangeRule?.endInclusive &&
        !v.rangeRule?.startExclusive &&
        !!v.rangeRule?.startInclusive,
    },
    {
      label: 'is before',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['endExclusive'],
      isSelected: v =>
        !!v.rangeRule?.endExclusive &&
        !v.rangeRule?.endInclusive &&
        !v.rangeRule?.startExclusive &&
        !v.rangeRule?.startInclusive,
    },
    {
      label: 'is on or before',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['endInclusive'],
      isSelected: v =>
        !v.rangeRule?.endExclusive &&
        !!v.rangeRule?.endInclusive &&
        !v.rangeRule?.startExclusive &&
        !v.rangeRule?.startInclusive,
    },
    {
      label: 'is between',
      isNot: false,
      ruleType: 'rangeRule',
      fieldKeys: ['startExclusive', 'endExclusive'],
      isSelected: v => {
        return (
          (!!v.rangeRule?.endInclusive || !!v.rangeRule?.endExclusive) &&
          (!!v.rangeRule?.startExclusive || !!v.rangeRule?.startInclusive)
        );
      },
    },
    {
      label: 'is not between',
      isNot: true,
      fieldKeys: ['startExclusive', 'endExclusive'],
      ruleType: 'rangeRule',
      isSelected: v => {
        return (
          (!!v.rangeRule?.endInclusive || !!v.rangeRule?.endExclusive) &&
          (!!v.rangeRule?.startExclusive || !!v.rangeRule?.startInclusive)
        );
      },
    },
    {
      label: 'is null',
      isNot: false,
      ruleType: 'eqRule',
    },
    {
      label: 'is not null',
      isNot: true,
      ruleType: 'eqRule',
    },
  ],
  nullValue: [
    {
      label: 'is null',
      isNot: false,
      ruleType: 'eqRule',
    },
    {
      label: 'is not null',
      isNot: true,
      ruleType: 'eqRule',
    },
  ],
};

export const getSelectedOperator = ({
  attribute: criterionAttribute,
  valueType,
  isNot,
}: {
  attribute?: InputMaybe<MetricsV1FilterCriterionAttributeCriterion>;
  isNot: boolean;
  valueType: ValueType;
}) => {
  const { attribute, ...values } = criterionAttribute ?? {};

  const ruleValue = ObjUtils.cleanNulls(values);
  const ruleType = Object.keys(ruleValue)?.[0] as RuleType;
  if (!criterionAttribute) return null;

  const selectedOp = OP[valueType].find(s => {
    if (s.isSelected)
      return s.isSelected(criterionAttribute) && s.isNot === isNot;
    return s.ruleType === ruleType && s.isNot === isNot;
  });
  return selectedOp;
};

export const Criteria = ({
  critera,
  onChange,
  onDelete,
  isNot,
  columns,
}: {
  isNot: boolean;
  critera?: MetricsV1FilterCriterionInput;
  onChange: (t: Partial<MetricFilterCriterionFragment>, isNot: boolean) => void;
  onDelete: () => void;
  columns?: FilterColumn[];
}) => {
  if (!critera?.attribute) return null;

  const { attribute, ...values } = critera?.attribute ?? {};
  const attrName = critera.attribute?.attribute ?? 'unknown';
  const column = columns?.find(c => c.name === attrName);
  const ruleValue = ObjUtils.cleanNulls(values);
  const ruleType = Object.keys(ruleValue)?.[0] as RuleType;
  const valueType = getValueType(values);
  if (!valueType || !critera?.attribute) return null;
  const availableOp = OP[valueType] ?? [];

  const selectedOp = getSelectedOperator({
    attribute: critera.attribute,
    isNot,
    valueType,
  });
  if (!selectedOp) return <span>Invalid operator</span>;

  const handleChangeOperator = (newOperator: OperatorOption) => {
    return () => {
      const isCurrentNull = valueType === 'nullValue';
      const isNewNull =
        newOperator.label === 'is null' || newOperator.label === 'is not null';

      if (
        critera &&
        newOperator.ruleType === ruleType &&
        newOperator.ruleType !== 'rangeRule' &&
        !isCurrentNull &&
        !isNewNull
      ) {
        if (newOperator.isNot !== isNot) {
          onChange(critera, newOperator.isNot);
        }
        return;
      }
      // Pick the value to bring to the new rule
      let currentValue = null;
      if (critera?.attribute?.eqRule) {
        currentValue = critera?.attribute?.eqRule.value;
      } else if (critera?.attribute?.setRule) {
        currentValue = critera?.attribute?.setRule.values[0];
      } else if (critera?.attribute?.rangeRule) {
        currentValue =
          critera?.attribute?.rangeRule.startExclusive ??
          critera?.attribute?.rangeRule.startInclusive ??
          critera?.attribute?.rangeRule.endExclusive ??
          critera?.attribute?.rangeRule.endInclusive;
      } else if (critera?.attribute?.likeRule) {
        currentValue = { stringValue: critera.attribute.likeRule.pattern };
      }

      // if no values are added, we cannot reuse the value-type
      if (!currentValue) {
        currentValue = {
          [valueType]: undefined,
        };
      }

      // When switching to a null check operator, use nullValue
      // When switching from null to a regular operator, default to string
      // (we don't have access to the original column type here)
      let targetValueType: ValueType;
      if (
        newOperator.label === 'is null' ||
        newOperator.label === 'is not null'
      ) {
        targetValueType = 'nullValue';
      } else if (valueType === 'nullValue') {
        targetValueType = 'stringValue';
      } else {
        targetValueType = valueType;
      }

      // Initialize nullValue with the required structure
      // When switching from null to regular, initialize with empty value
      let targetValue;
      if (targetValueType === 'nullValue') {
        targetValue = { nullValue: { dummyField: 'null' } };
      } else if (valueType === 'nullValue') {
        targetValue = { [targetValueType]: undefined };
      } else {
        targetValue = currentValue;
      }

      if (newOperator.ruleType === 'eqRule') {
        onChange(
          {
            attribute: {
              attribute: attrName,
              eqRule: {
                value: targetValue,
              },
            },
          },
          newOperator.isNot,
        );
      } else if (newOperator.ruleType === 'setRule') {
        onChange(
          {
            attribute: {
              attribute: attrName,
              setRule: {
                values: [targetValue],
              },
            },
          },
          newOperator.isNot,
        );
      } else if (newOperator.ruleType === 'rangeRule') {
        const [firstKey, secondKey] = newOperator.fieldKeys;
        onChange(
          {
            attribute: {
              attribute: attrName,
              rangeRule: {
                [firstKey]: targetValue,
                ...(secondKey && {
                  [secondKey]: {
                    [targetValueType]: undefined,
                  },
                }),
              },
            },
          },
          newOperator.isNot,
        );
      } else if (newOperator.ruleType === 'likeRule') {
        const likePattern =
          targetValue?.stringValue?.toString() ??
          targetValue?.numberValue?.toString() ??
          '';
        onChange(
          {
            attribute: {
              attribute: attrName,
              likeRule: { pattern: likePattern },
            },
          },
          newOperator.isNot,
        );
      }
    };
  };

  return (
    <Box>
      <Chip
        label={
          <Box display="flex" gridGap={8} alignItems="center">
            <Typography variant="body2">
              {critera.attribute.attribute}
            </Typography>

            <ContextMenu
              closeOnSelect
              renderButton={p => (
                <Button
                  {...p}
                  endIcon={<ExpandMore color="inherit" />}
                  size="small"
                  variant="text"
                >
                  {selectedOp?.label}
                </Button>
              )}
              renderMenu={() =>
                availableOp.map(option => (
                  <MenuItem
                    key={option.label}
                    divider
                    onClick={handleChangeOperator(option)}
                  >
                    {option.label}
                  </MenuItem>
                ))
              }
            />

            {ruleType === 'eqRule' &&
              valueType &&
              valueType !== 'nullValue' && (
                <MetricFilterEqRule
                  valueType={valueType}
                  attribute={attrName}
                  value={critera.attribute.eqRule}
                  onChange={v => onChange(v, isNot)}
                  column={column}
                />
              )}
            {ruleType === 'setRule' && valueType && (
              <MetricFilterSetRule
                valueType={valueType}
                attribute={attrName}
                value={critera.attribute.setRule}
                onChange={v => onChange(v, isNot)}
                column={column}
              />
            )}
            {ruleType === 'rangeRule' && valueType && (
              <MetricFilterRangeRule
                valueType={valueType}
                operator={selectedOp}
                attribute={attrName}
                value={critera.attribute.rangeRule}
                onChange={v => onChange(v, isNot)}
                column={column}
              />
            )}
            {ruleType === 'likeRule' && (
              <MetricFilterLikeRule
                attribute={attrName}
                value={critera.attribute.likeRule}
                onChange={v => onChange(v, isNot)}
              />
            )}
          </Box>
        }
        onDelete={onDelete}
        deleteIcon={<CancelIcon name="delete-icon" />}
      />
    </Box>
  );
};
